import { useState } from 'react'
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Breadcrumbs from '@mui/material/Breadcrumbs'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Link from '@mui/material/Link'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import type { Usage } from '../api/authoring'
import { useGetAuthoringComponentQuery, useGetManifestQuery, useListUsagesQuery } from '../api/authoring'
import { problemMessage } from '../api/baseApi'
import { useIntent } from '../api/useIntent'
import { useSelectedNode } from '../api/useSelectedNode'
import { arranges } from '../model/composition'
import { LISTING_LAG } from '../model/labels'
import { decodeLinkPath, encodeLinkPath, type LinkPath } from '../model/link'
import { typeProse } from '../model/manifest'
import { nameOf } from '../model/name'
import { routes } from '../model/routes'
import { CompositionEditor } from '../ui/CompositionEditor'
import { DeleteComponentDialog } from '../ui/DeleteComponentDialog'
import { NodePanel } from '../ui/NodePanel'
import { Loading, QueryError } from '../ui/QueryState'
import { UndoButton } from '../ui/UndoButton'

/**
 * The component's own node, for the face that edits it.
 *
 * A constant rather than a literal in the call, so the resolution is not
 * recomputed on every render of a screen that has one anyway.
 */
const OWN: LinkPath = []

/**
 * One standalone component, edited on its own.
 *
 * The page editor addresses a node *inside* a page and has to work out
 * where an intent goes; here there is nothing to work out. A standalone
 * component is its own stream, the command id is the id in the URL and
 * the link path is empty — which is what makes this the natural home for
 * the operations that belong to a component rather than to a placement:
 * the uploader, and the delete that follows.
 *
 * It is reached from a library, and from nowhere else yet. A placement in
 * a page links here through the page editor's own panel, which edits the
 * same stream with the same editor — deliberately, because a linked
 * component's content is one thing wherever it is placed, and two screens
 * that disagreed about that would be two models of one document.
 *
 * **Three faces, because they were three sections one scroll long.** The
 * type's own form, the children under it and the list of what holds it
 * were stacked, so an operator arranging a Container scrolled past a
 * spacing control to reach the tree and past the tree to find out what
 * would break if they deleted it. Each is now an address:
 *
 *   Composition — the children, arranged, with the selected one's editor
 *                 beside them. Drawn only where the type arranges
 *                 anything: a Content or a Media has no regions, and a
 *                 tab over nothing is worse than no tab.
 *   Fields      — the component's own: its picture where the type holds
 *                 one, the edges it declares, and its type's editor.
 *   Held by     — what places this, and the delete. They are one
 *                 question — what a delete would take away is exactly
 *                 that list — so they are one face.
 *
 * **The bare address is the face the type has.** A Container is arranged
 * and a Content is written, so the shortest URL opens composition where
 * there is one and the fields where there is not. A component's type is
 * fixed when it is created, so one address always opens the same face.
 *
 * Selection is `?sel=`, the same query the page editor holds it in and
 * for the same reasons: a placement is linkable, it survives a reload,
 * and it rides across a tab change so an operator who checks what holds
 * this component comes back to the block they had open.
 */
export function ComponentEditor({ face }: { face?: 'compose' | 'fields' | 'held' }) {
  const { tenant = '', id = '' } = useParams()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const selection = decodeLinkPath(params.get('sel'))
  const manifest = useGetManifestQuery({ tenant })
  const component = useGetAuthoringComponentQuery({ tenant, id })
  const held = useListUsagesQuery({ tenant, id })
  const { send, isLoading: sending, error: sendError } = useIntent(tenant)
  const [deleting, setDeleting] = useState(false)

  const select = (path: LinkPath) => {
    const next = new URLSearchParams(params)
    if (path.length === 0) next.delete('sel')
    else next.set('sel', encodeLinkPath(path))
    setParams(next, { replace: true })
  }

  // Twice, and they answer different questions. The tree's panel edits
  // whatever is selected; the fields face edits this component, whatever
  // the tree has selected — a reader who opens a block and then switches
  // to Fields is asking for the component's, not the block's.
  const selected = useSelectedNode(tenant, component.data, selection)
  const own = useSelectedNode(tenant, component.data, OWN)

  if (manifest.isLoading || component.isLoading) return <Loading label="Reading the component…" />
  if (component.error) return <QueryError error={component.error} title="Could not read this component" />
  if (!component.data) return null

  const document = component.data
  const usages = held.data?.usages ?? []
  const hasRegions = arranges(manifest.data, document.type)
  // Which face is showing. An address that names one wins; the bare
  // address is the type's own answer, and a composition face asked for
  // on a type that arranges nothing falls to the fields rather than
  // drawing an empty tree.
  const showing = (face === 'compose' && !hasRegions ? 'fields' : face) ?? (hasRegions ? 'compose' : 'fields')
  // What it calls itself, from the same declaration the library row it
  // was opened from reads. A uuid in the heading was this screen knowing
  // less about the component than the list behind it.
  const name = nameOf(document, manifest.data)
  const prose = typeProse(manifest.data, document.type)
  const search = params.toString()

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Breadcrumbs>
          <Link component={RouterLink} to={routes.components(tenant, document.type)}>
            {prose.plural}
          </Link>
          <Typography color="text.primary">{name ?? document.id}</Typography>
        </Breadcrumbs>
        <Typography variant="h1">{name ?? document.id}</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip size="small" variant="outlined" label={prose.title} />
          {/* The id stays, as a fact rather than as the name. Build mode
              is where the model is visible on purpose — an operator does
              ask which component this is — and a chip is where it can be
              read without being read *first*. */}
          <Chip size="small" variant="outlined" label={`${document.id} · r${document.revision}`} />
          {document.tombstoned && <Chip size="small" color="error" label="deleted" />}
        </Stack>
        {/* A tombstone is a revision like any other, so undo brings a
            deleted component back — which is the claim the delete dialog
            already makes and had nothing to make good on. It stays in
            the header rather than moving to the face that deletes: it is
            used seconds after a mistake made on any of them. */}
        <Stack direction="row">
          <UndoButton tenant={tenant} document={document} manifest={manifest.data} disabled={sending} />
        </Stack>

        {/* Links, not buttons — a face is an address, so each tab is
            somewhere to go and a colleague can be sent any of them. The
            query rides along, so a tab change keeps the selection. */}
        <Tabs value={showing} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          {hasRegions && (
            <Tab
              label="Composition"
              value="compose"
              component={RouterLink}
              to={{ pathname: routes.component(tenant, id), search }}
            />
          )}
          <Tab
            label="Fields"
            value="fields"
            component={RouterLink}
            to={{
              pathname: hasRegions ? routes.componentFields(tenant, id) : routes.component(tenant, id),
              search,
            }}
          />
          <Tab
            label="Held by"
            value="held"
            component={RouterLink}
            to={{ pathname: routes.componentHeldBy(tenant, id), search }}
          />
        </Tabs>
      </Stack>

      {document.tombstoned && (
        <Alert severity="warning">
          This component is deleted, and cannot be undone from here. It is gone from every library and every picker,
          and its stream is untouched — which is why every placement pinned to a revision of it still resolves.
        </Alert>
      )}

      {sendError && <Alert severity="error">{problemMessage(sendError, 'The intent was refused.')}</Alert>}

      {showing === 'compose' ? (
        /* The tree and the selected node's editor, in the layout the
           page editor uses — the two screens arrange a document the same
           way and there is nothing about a standalone one that makes it
           read differently. */
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 2fr) minmax(360px, 3fr)' },
            gap: 3,
            alignItems: 'start',
          }}
        >
          <Box>
            <CompositionEditor
              tenant={tenant}
              document={document}
              manifest={manifest.data}
              selection={selection}
              onSelect={select}
              send={send}
              busy={sending}
            />
          </Box>

          <NodePanel
            tenant={tenant}
            manifest={manifest.data}
            selection={selection}
            selected={selected}
            document={document}
            onSelect={select}
            send={send}
            busy={sending}
            editsRoot={false}
            empty={
              <Typography variant="caption" color="text.secondary">
                Select a placement to edit it. This component&rsquo;s own fields are on the Fields tab.
              </Typography>
            }
          />
        </Box>
      ) : showing === 'held' ? (
        <HeldBy
          tenant={tenant}
          usages={usages}
          loading={held.isLoading}
          error={held.error}
          onDelete={() => setDeleting(true)}
          deletable={!sending && document.tombstoned !== true}
        />
      ) : (
        /* The component's own fields, in the width the shell gives
           them. They were capped at 720px, as the page's settings form
           once was, and for the same reason it stopped being: a field
           that grows — a rich text body, a designer's outline beside
           its panel — was held to a column with the window empty
           beside it. */
        <Box>
          <NodePanel
            tenant={tenant}
            manifest={manifest.data}
            selection={OWN}
            selected={own}
            document={document}
            onSelect={select}
            send={send}
            busy={sending}
            editsRoot
            empty={null}
          />
        </Box>
      )}

      <DeleteComponentDialog
        tenant={tenant}
        open={deleting}
        document={document}
        manifest={manifest.data}
        onClose={() => setDeleting(false)}
        onDeleted={() => {
          setDeleting(false)
          // Back to the library it came from. Not to the listing's
          // *contents*, which will still show it until the projection
          // catches up — the component's own document already says
          // `tombstoned`, and that is the fact.
          navigate(routes.components(tenant, document.type))
        }}
      />
    </Stack>
  )
}

/**
 * What places this component, and the way out of the library.
 *
 * They are one face because they are one question: what a delete would
 * take away is exactly this list, and the two were already drawn in one
 * box for that reason before either was a face.
 */
function HeldBy({
  tenant,
  usages,
  loading,
  error,
  onDelete,
  deletable,
}: {
  tenant: string
  usages: Usage[]
  loading: boolean
  error: unknown
  onDelete: () => void
  deletable: boolean
}) {
  return (
    <Paper sx={{ p: 2.5 }}>
      <Stack spacing={1.5}>
        {loading && <Loading label="Walking composition backwards…" />}
        {Boolean(error) && <QueryError error={error} title="Could not look up what holds this" />}

        {!loading && !error && usages.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No component places this one — as far as this lookup can see.
          </Typography>
        )}

        {usages.length > 0 && (
          <List dense disablePadding>
            {usages.map((usage) => (
              <ListItemButton key={usage.id} component={RouterLink} to={routes.component(tenant, usage.id)}>
                <ListItemText
                  primary={usage.label ?? usage.id}
                  secondary={
                    usage.placements === 1
                      ? usage.pinned === 1
                        ? 'one placement, pinned to a revision'
                        : 'one placement, following the latest revision'
                      : `${usage.placements} placements, ${usage.pinned} pinned`
                  }
                />
                <Chip size="small" variant="outlined" label={usage.type} />
              </ListItemButton>
            ))}
          </List>
        )}

        {/* Stated wherever this list is shown, not only in the delete
            dialog: an author reading an empty list as "nothing uses
            this" is wrong in exactly one direction, and it is the
            direction that loses an image off a page. */}
        <Typography variant="caption" color="text.secondary">
          Only placements are visible here. An image named by URL — in a background, or embedded in markdown — is not a
          child, so it has no pin state and no entry. {LISTING_LAG}
        </Typography>

        <Divider />
        <Stack direction="row">
          <Button color="error" startIcon={<DeleteOutlineIcon />} onClick={onDelete} disabled={!deletable}>
            Delete
          </Button>
        </Stack>
      </Stack>
    </Paper>
  )
}
